<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <form @submit="createIndexSource" class="flex flex-col h-full">
      <div v-bar class="flex-1 min-h-0 overflow-y-auto">
        <div class="min-h-full">
          <div class="border-solid border-b border-0 border-gray-border mt-8 inline-block px-8 pb-8">
            <h3 class="font-semibold">
              <span class="cursor-pointer text-gray-dark hover:text-primary-focus hover:underline"
                    @click="$emit('display-index-editor')"
              >{{ index.name }}</span>
              <span class="text-gray ml-1">/</span>
              <span class="text-black ml-1">Add new source</span>
            </h3>
          </div>

          <div class="px-8 pb-8 mt-5">
            <with-error-message :show-error="isIndexSourceNameError">
              <base-label id="indexSourceName" label="Source Name" :label-classes="'font-medium'"/>
              <base-input id="indexSourceName"
                          name="indexSourceName"
                          placeholder="e.g. Sales campaign feed"
                          variant="editor"
                          type="string"
                          class="w-full border-gray-border py-px mt-1 h-10"
                          :class="{'border-focus-error': isIndexSourceNameError}"
                          v-model.trim="indexSourceName"
              />
            </with-error-message>

            <settings-indexes-forms :fileSettings.sync="fileSettings"
                                    :active-form.sync="activeTab"
                                    :xmlFeedSettings.sync="xmlFeedSettings"
                                    :webPageSettings.sync="webPageSettings"
                                    :show-field-selection="showFieldSelection"
                                    class="mt-5"
                                    @update:available-tags="availableTags = $event"
            />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 mb-6 px-8 flex-shrink-0">
        <busy-button id="createButton"
                     :variant="createButtonVariant"
                     class="col-span-1"
                     :is-loading="isLoading"
                     :is-disabled="isLoading"
                     @click="createIndexSource"
        >
          <span class="w-full">{{ createButtonText }}</span>
        </busy-button>
      </div>
    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import indexSourcesService from '@/services/amio/index-sources.service'
import SettingsIndexesForms from '@/components/settings/indexes/SettingsIndexesForms.vue'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import toaster from '@/utils/toaster'
import previewModule from '@/store/modules/preview.module'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'
import fileService from '@/services/amio/file.service'
import { SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { CREATE_BUTTON_LABELS } from '@/utils/text.constants'
import errorUtils from '@/utils/error.utils'
import { applyFieldSelection } from '@/utils/xml-source-config.utils'

export default {
  name: 'SettingsIndexSourceCreate',
  components: {
    BusyButton,
    SettingsIndexesForms,
    WithErrorMessage
  },
  props: {
    index: {
      type: Object,
      required: true
    },
    sourceType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      indexSourceName: '',
      webPageSettings: {
        url: '',
        format: 'txt',
        prompt: null,
        headerKey: null,
        headerValue: null
      },
      xmlFeedSettings: {
        url: '',
        item: '',
        itemName: '',
        itemId: '',
        category: '',
        vectorizationSkipTags: [],
        manualConfig: false, // opt-in manual field configuration; default is derive-from-feed
        fieldSelection: { embed_fields: [], payload_fields: {}, group_key: [] }
      },
      fileSettings: {
        file: null,
        url: ''
      },
      availableTags: [],
      isLoading: false,
      KNOWLEDGE_BASES,
      createButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      createButtonText: CREATE_BUTTON_LABELS.DEFAULT
    }
  },
  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    activeTab: {
      get() {
        return this.sourceType
      },
      set(tab) {
        this.$emit('update:sourceType', tab)
      }
    },

    isIndexSourceNameError() {
      return this.errorDescription?.includes('Source Name')
    },

    // Same gate as SettingsIndexSourceEditor: Qdrant XML only, once the feed preview has tags.
    showFieldSelection() {
      return this.index.provider === 'qdrant' &&
        this.sourceType === KNOWLEDGE_BASES.XML_FEED &&
        this.availableTags.length > 0
    }
  },
  watch: {
    showFieldSelection(isShown) {
      if (!isShown) this.updateFieldSelection({ embed_fields: [], payload_fields: {}, group_key: [] })
    }
  },
  methods: {
    hasEmptyField: (fields) => fields.some(field => isEmpty(field) || !field),

    updateFieldSelection(fieldSelection) {
      this.xmlFeedSettings = { ...this.xmlFeedSettings, fieldSelection }
    },

    createIndexSource(e) {
      e.preventDefault()
      if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
        this.createIndexXMLSource()
      }
      if (this.sourceType === KNOWLEDGE_BASES.FILE) {
        this.createIndexFileSource()
      }
      if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
        this.createIndexWebPageSource()
      }
    },
    async createIndexFileSource() {
      this.isLoading = true
      try {
        const newSource = { type: KNOWLEDGE_BASES.FILE, name: this.indexSourceName }
        if (this.fileSettings.file) {
          const response = await fileService.create(this.fileSettings.file)
          newSource.format = response.format
          newSource.url = response.url
        } else {
          newSource.format = this.fileSettings.url.split('.').pop()
          newSource.url = this.fileSettings.url
        }
        await indexSourcesService.create(this.index.id, newSource)
        this.$emit('post-process-index-source-create')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },
    async createIndexWebPageSource() {
      this.isLoading = true
      try {
        const newSource = {
          type: 'web_scraping',
          format: 'html',
          name: this.indexSourceName,
          url: this.webPageSettings.url,
          config: {
            prompt: this.webPageSettings.prompt,
            format: this.webPageSettings.format
          }
        }
        if (this.webPageSettings.headerKey !== null || this.webPageSettings.headerValue !== null) {
          newSource.config.header = {
            key: this.webPageSettings.headerKey,
            value: this.webPageSettings.headerValue
          }
        }
        await indexSourcesService.create(this.index.id, newSource)
        this.error = null
        this.$emit('post-process-index-source-create')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },
    async createIndexXMLSource() {
      this.isLoading = true
      try {
        const { url, item, itemName, itemId, category, vectorizationSkipTags, fieldSelection } = this.xmlFeedSettings
        const newSource = { type: 'url', format: 'xml', name: this.indexSourceName, url }
        // Same rule as the index-create flow (index-create.mixin buildXmlSource): driven by the
        // explicit "Configure fields manually" toggle. Untoggled means derive, which omitting
        // `config` is the signal for.
        if (this.xmlFeedSettings.manualConfig) {
          newSource.config = applyFieldSelection({
            tag_mapping: { item: item, item_name: itemName, item_id: itemId },
            vectorization_skip_tags: vectorizationSkipTags
          }, fieldSelection, category)
        }
        await indexSourcesService.create(this.index.id, newSource)
        this.error = null
        this.$emit('post-process-index-source-create')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.createButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      this.createButtonText = CREATE_BUTTON_LABELS[buttonState]
    },

    handleError(err) {
      if (err.message === 'Payload Too Large') {
        this.error = { description: 'File exceeds 1 MB limit.' }
        return
      }

      const errorMessage = head(err.response.data.details?.errors).message || err.message
      if (errorUtils.isIndexCreateUpdateError(errorMessage)) {
        this.error = { description: errorMessage }
        return
      }

      this.error = errorMessage
      toaster.error(errorMessage)
    }
  },

  mounted() {
    this.error = null
  }

}
</script>
